import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { 
  X, Globe, MapPin, Target, Shield, Calendar, 
  Activity, ExternalLink, AlertTriangle, Network, ShieldAlert, FileText, Server, AlertOctagon, PieChart as PieIcon,
  Plus, Minus, RefreshCcw 
} from "lucide-react";
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { redirectToLogin } from "../utils/auth";

const COLORS = ["#0ea5e9", "#22c55e", "#eab308", "#f97316", "#ef4444", "#a855f7"];
const FROSTED_CARD = "bg-[#06101e]/60 backdrop-blur-sm border border-white/5 rounded-xl";
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a]/95 border border-sky-500/20 p-3 rounded-lg shadow-2xl backdrop-blur-none z-50">
        <p className="text-neutral-200 text-xs font-medium mb-1">{label || payload[0].name}</p>
        <p className="text-sky-400 font-bold text-sm">
          {payload[0].value} <span className="text-neutral-500 font-normal text-xs">incidents</span>
        </p>
      </div>
    );
  }
  return null;
};

// --- HELPER FUNCTIONS ---

function extractThreatFeedAccess(json) {
  try {
    if (!json || typeof json !== "object") return false;
    if (Object.prototype.hasOwnProperty.call(json, "threat_feed")) {
      const v = json.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    if (json.data && typeof json.data === "object" && "threat_feed" in json.data) {
      const v = json.data.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    if (json.subscription && typeof json.subscription === "object" && "threat_feed" in json.subscription) {
      const v = json.subscription.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
  } catch (_) {}
  return false;
}

function UpgradeCallout() {
  const go = () => {
    try { window.location.href = "/upgrades"; } catch {}
  };
 return (
    <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 mt-0.5" />
        <div>
          <div className="font-semibold">Threat Feed is locked</div>
          <div className="text-sm opacity-90">
            Your current subscription doesn’t include Threat Feed. Upgrade to
            unlock full intel details, sources and historical context.
          </div>
          <div className="mt-2">
            <button
              onClick={go}
              className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10"
            >
              Upgrade plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- MAIN COMPONENT ---

export default function DetailSidebarActor({ open, onClose, slug, jwtToken, apiUrl }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [hasThreatFeedAccess, setHasThreatFeedAccess] = useState(null); 

  // --- MAP STATES ---
  const [mapTooltip, setMapTooltip] = useState(null);
  const [mapPosition, setMapPosition] = useState({ coordinates: [0, 20], zoom: 1 });

  // Map Controls
  const handleZoomIn = () => {
    if (mapPosition.zoom >= 4) return;
    setMapPosition(pos => ({ ...pos, zoom: pos.zoom * 1.5 }));
  };

  const handleZoomOut = () => {
    if (mapPosition.zoom <= 1) return;
    setMapPosition(pos => ({ ...pos, zoom: pos.zoom / 1.5 }));
  };

  const handleResetMap = () => {
    setMapPosition({ coordinates: [0, 20], zoom: 1 });
  };

  const handleMoveEnd = (position) => {
    setMapPosition(position);
  };

  useEffect(() => {
    if (!open || !slug) return;
    setLoading(true);
    setError("");
    setData(null);
    setHasThreatFeedAccess(null);
    setMapTooltip(null);
    setMapPosition({ coordinates: [0, 20], zoom: 1 }); // Reset map

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
      body: JSON.stringify({ metric: "detail", slug }),
    })
      .then(async (res) => {
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (res.status === 403) {
            setHasThreatFeedAccess(false);
            return null; 
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        return json;
      })
      .then((j) => {
        if (!j) return; 

        if (j.status === "error") {
            if (j.message && j.message.toLowerCase().includes("not allowed")) {
                setHasThreatFeedAccess(false);
            } else {
                throw new Error(j.message);
            }
        } else {
            setData(j.data);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, slug, jwtToken, apiUrl]);

  const chartData = (jsonObj) => {
    if (!jsonObj || typeof jsonObj !== 'object') return [];
    return Object.entries(jsonObj)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  // These are already sorted descending by value
  const geoData = data?.profile?.stats_geo ? chartData(data.profile.stats_geo) : [];
  const vectorData = data?.profile?.stats_vector ? chartData(data.profile.stats_vector) : [];
  const industryData = data?.profile?.stats_industry ? chartData(data.profile.stats_industry) : [];

  const locked = hasThreatFeedAccess === false;

  return (
    <>
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        {/* BACKDROP */}
        <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col bg-[#06101e] border-l border-sky-500/20 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                    
                    {/* Header */}
                    <div className="relative px-6 py-6 border-b border-sky-500/10 bg-gradient-to-b from-[#0a1224] to-[#06101e]">
                      <div className="flex items-start justify-between">
                        <div>
                          {loading ? (
                             <div className="h-8 w-48 bg-white/10 rounded animate-pulse" />
                          ) : (
                             <Dialog.Title className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                               {locked ? "Restricted Access" : (data?.profile?.name || "Loading...")}
                               {!locked && data?.profile?.status === "Active" && (
                                  <span className="relative flex h-3 w-3">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                                  </span>
                               )}
                             </Dialog.Title>
                          )}
                          
                          {!locked && (
                              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-neutral-400">
                                {!loading && data && (
                                    <>
                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/5">
                                        <Activity className="h-3.5 w-3.5 text-sky-400" />
                                        <span className={data?.profile?.status === "Active" ? "text-emerald-400 font-medium" : "text-neutral-400"}>
                                            {data?.profile?.status || "Unknown"}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Calendar className="h-3.5 w-3.5 text-neutral-500" />
                                        <span>First Seen: <span className="text-neutral-200">{formatDate(data?.profile?.first_seen)}</span>  </span>
                                        <span>Last Seen: <span className="text-neutral-200">{formatDate(data?.profile?.last_seen)}</span>  </span>
                                    </div>
                                    </>
                                )}
                              </div>
                          )}
                        </div>
                        <button onClick={onClose} className="rounded-lg p-2 text-neutral-400 hover:text-white hover:bg-white/10 transition">
                          <X className="h-6 w-6" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-sky-500/30 to-transparent" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-thin scrollbar-thumb-sky-900/50 scrollbar-track-transparent">
                      
                      {loading && (
                         <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                               <div className="h-24 bg-white/5 rounded-xl animate-pulse" />
                               <div className="h-24 bg-white/5 rounded-xl animate-pulse" />
                            </div>
                            <div className="h-64 bg-white/5 rounded-xl animate-pulse" />
                         </div>
                      )}

                      {/* --- LOCKED STATE --- */}
                      {locked && !loading && (
                          <UpgradeCallout />
                      )}

                      {/* --- ERROR STATE --- */}
                      {error && !locked && !loading && (
                         <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/10 text-red-200 flex items-center gap-3">
                            <AlertTriangle className="h-5 w-5" /> {error}
                         </div>
                      )}
                      
                      {/* --- DATA DISPLAY --- */}
                      {data && !loading && !locked && (
                        <>
                           {/* Key Stats */}
                           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div className={`p-5 ${FROSTED_CARD} relative overflow-hidden group`}>
                                 <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                                    <Target className="h-16 w-16 text-sky-500" />
                                 </div>
                                 <div className="text-xs uppercase text-sky-400 font-bold tracking-widest mb-1">Confirmed Victims</div>
                                 <div className="text-3xl font-bold text-white tracking-tight">{data.profile.total_incidents.toLocaleString()}</div>
                              </div>
                              <div className={`p-5 ${FROSTED_CARD} relative overflow-hidden group`}>
                                 <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                                    <Shield className="h-16 w-16 text-rose-500" />
                                 </div>
                                 <div className="text-xs uppercase text-rose-400 font-bold tracking-widest mb-1">Primary TTP</div>
                                 <div className="text-xl font-bold text-white tracking-tight truncate mt-1">
                                    {/* UPDATED: Uses the sorted vectorData to find highest */}
                                    {vectorData.length > 0 ? vectorData[0].name : "Unknown"}
                                 </div>
                              </div>
                           </div>
                           
                           {/* Charts Section */}
                           <div className="space-y-6">
                              
                              {/* Industry Bar Chart */}
                              <div className={`${FROSTED_CARD} p-6`}>
                                 <h3 className="text-white font-semibold flex items-center gap-2 mb-6"><Server className="h-4 w-4 text-sky-400" /> Targeted Industries</h3>
                                 <div className="h-[250px] w-full -ml-4">
                                    {industryData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart layout="vertical" data={industryData}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#334155" opacity={0.5} />
                                                <XAxis type="number" hide />
                                                <YAxis dataKey="name" type="category" width={140} tick={{fill: '#e2e8f0', fontSize: 12}} tickLine={false} axisLine={false} />
                                                <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(255,255,255,0.05)'}} />
                                                <Bar dataKey="value" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={16}>
                                                    {industryData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-sm text-neutral-500">
                                            No industry data available
                                        </div>
                                    )}
                                 </div>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                 
                                 {/* --- ZOOMABLE MAP VISUALIZATION --- */}
                                 <div className={`${FROSTED_CARD} p-0 flex flex-col relative overflow-hidden min-h-[300px]`}>
                                     <div className="absolute top-0 left-0 p-5 z-20 pointer-events-none">
                                       <h3 className="text-white font-semibold flex items-center gap-2 drop-shadow-md">
                                         <Globe className="h-4 w-4 text-emerald-400" /> Geography
                                       </h3>
                                     </div>

                                     {/* Map Controls */}
                                     <div className="absolute top-4 right-4 flex flex-col gap-2 z-20">
                                         <button onClick={handleZoomIn} className="p-1.5 bg-slate-900/80 hover:bg-sky-600 text-white rounded-lg border border-white/10 shadow-lg backdrop-blur transition-all">
                                             <Plus className="h-4 w-4" />
                                         </button>
                                         <button onClick={handleZoomOut} className="p-1.5 bg-slate-900/80 hover:bg-sky-600 text-white rounded-lg border border-white/10 shadow-lg backdrop-blur transition-all">
                                             <Minus className="h-4 w-4" />
                                         </button>
                                         <button onClick={handleResetMap} className="p-1.5 bg-slate-900/80 hover:bg-sky-600 text-white rounded-lg border border-white/10 shadow-lg backdrop-blur transition-all" title="Reset View">
                                             <RefreshCcw className="h-4 w-4" />
                                         </button>
                                     </div>
                                     
                                     <div className="flex-1 w-full h-full">
                                         {geoData.length > 0 ? (
                                             <>
                                               <ComposableMap 
                                                  projection="geoMercator" 
                                                  projectionConfig={{ scale: 100 }} 
                                                  className="w-full h-full outline-none"
                                                  height={280}
                                               >
                                                  <ZoomableGroup 
                                                    zoom={mapPosition.zoom}
                                                    center={mapPosition.coordinates}
                                                    onMoveEnd={handleMoveEnd}
                                                  >
                                                    <Geographies geography={GEO_URL}>
                                                      {({ geographies }) =>
                                                        geographies.map((geo) => {
                                                          const hit = geoData.find(d => 
                                                            d.name === geo.properties.name || 
                                                            (d.name === "USA" && geo.properties.name === "United States of America") ||
                                                            (d.name === "UK" && geo.properties.name === "United Kingdom")
                                                          );

                                                          return (
                                                            <Geography
                                                              key={geo.rsmKey}
                                                              geography={geo}
                                                              onMouseEnter={() => { if(hit) setMapTooltip(hit); }}
                                                              onMouseLeave={() => { setMapTooltip(null); }}
                                                              style={{
                                                                default: {
                                                                    fill: hit ? "#0ea5e9" : "#1e293b",
                                                                    stroke: hit ? "#38bdf8" : "#334155",
                                                                    strokeWidth: 0.5,
                                                                    outline: "none",
                                                                    filter: hit ? "drop-shadow(0 0 6px rgba(14,165,233,0.5))" : "none",
                                                                    opacity: hit ? 1 : 0.4
                                                                },
                                                                hover: {
                                                                    fill: hit ? "#38bdf8" : "#334155",
                                                                    stroke: "#cbd5e1",
                                                                    strokeWidth: 0.75,
                                                                    outline: "none",
                                                                    opacity: 1
                                                                },
                                                                pressed: {
                                                                    fill: "#0284c7",
                                                                    outline: "none"
                                                                }
                                                              }}
                                                            />
                                                          );
                                                        })
                                                      }
                                                    </Geographies>
                                                  </ZoomableGroup>
                                               </ComposableMap>
                                               
                                               {/* Floating Map Legend/Tooltip */}
                                               {mapTooltip && (
                                                  <div className="absolute bottom-4 left-4 right-4 bg-sky-950/90 border border-sky-500/30 p-3 rounded-lg backdrop-blur-md shadow-xl animate-in fade-in slide-in-from-bottom-2 pointer-events-none z-30">
                                                      <div className="flex justify-between items-center">
                                                          <span className="text-white font-medium text-sm">{mapTooltip.name}</span>
                                                          <span className="text-sky-300 font-bold font-mono">{mapTooltip.value} <span className="text-sky-500/70 text-xs">Hits</span></span>
                                                      </div>
                                                  </div>
                                               )}
                                             </>
                                         ) : (
                                             <div className="flex h-full w-full flex-col items-center justify-center text-neutral-500 space-y-2">
                                                 <Globe className="h-8 w-8 opacity-20" />
                                                 <span className="text-xs">No geography data</span>
                                             </div>
                                         )}
                                     </div>
                                 </div>

                                 {/* --- VECTORS RADAR CHART --- */}
                                 <div className={`${FROSTED_CARD} p-5 flex flex-col`}>
                                     <h3 className="text-white font-semibold flex items-center gap-2 mb-4"><Shield className="h-4 w-4 text-red-400" /> Vectors</h3>
                                     <div className="h-48 w-full relative">
                                         {vectorData.length > 0 ? (
                                             <ResponsiveContainer width="100%" height="100%">
                                                <RadarChart cx="50%" cy="50%" outerRadius="65%" data={vectorData}>
                                                    <PolarGrid stroke="#334155" strokeOpacity={0.2} />
                                                    <PolarAngleAxis 
                                                      dataKey="name" 
                                                      tick={{ fill: '#94a3b8', fontSize: 10 }} 
                                                    />
                                                    <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} />
                                                    <Radar
                                                      name="Vectors"
                                                      dataKey="value"
                                                      stroke="#38bdf8"
                                                      strokeWidth={2}
                                                      fill="#0ea5e9"
                                                      fillOpacity={0.3}
                                                    />
                                                    <Tooltip content={<CustomTooltip />} cursor={false} />
                                                </RadarChart>
                                             </ResponsiveContainer>
                                         ) : (
                                             <div className="flex h-full w-full flex-col items-center justify-center text-neutral-500 space-y-2">
                                                 <PieIcon className="h-8 w-8 opacity-20" />
                                                 <span className="text-xs">No vector data</span>
                                             </div>
                                         )}
                                     </div>
                                 </div>
                              </div>
                           </div>

                           {/* 3. Recent Activity */}
                           <div className="pt-4">
                              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <Activity className="h-5 w-5 text-sky-400" /> Recent Operations
                              </h3>
                              
                              <div className="relative border-l border-white/10 ml-3 space-y-8">
                                 {data.feed && data.feed.map((item, idx) => {
                                    const hasDomain = item.victim_domain || item.victim_site;
                                    const hasDistinctTitle = item.title && item.title !== item.victim_organization;
                                    
                                    return (
                                    <div key={idx} className="relative pl-8 group">
                                       <div className="absolute -left-[5px] top-6 h-2.5 w-2.5 rounded-full bg-sky-900 border border-sky-500 group-hover:bg-sky-400 transition-colors shadow-[0_0_10px_rgba(14,165,233,0.3)]"></div>
                                       
                                       <div className={`${FROSTED_CARD} p-5 transition-all hover:bg-white/10 hover:border-sky-500/30`}>
                                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                             <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                   <div className="font-bold text-lg text-white group-hover:text-sky-300 transition-colors truncate">
                                                      {item.victim_organization || "Unknown Target"}
                                                   </div>
                                                   {item.category && (
                                                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-500/10 text-rose-300 border border-rose-500/20 whitespace-nowrap">
                                                         {item.category}
                                                      </span>
                                                   )}
                                                   {item.published_url && (
                                                       <button 
                                                            onClick={() => setRedirectUrl(item.published_url)}
                                                            className="ml-2 p-1.5 rounded-md bg-white/5 border border-white/5 text-neutral-400 hover:text-red-300 hover:bg-red-500/10 hover:border-red-500/30 transition-all"
                                                            title="View Source (External)"
                                                       >
                                                            <ExternalLink className="h-3.5 w-3.5" />
                                                       </button>
                                                   )}
                                                </div>

                                                {hasDistinctTitle && (
                                                   <div className="mt-1 flex items-start gap-1.5">
                                                      <FileText className="h-3.5 w-3.5 text-neutral-500 mt-0.5 shrink-0" />
                                                      <span className="text-sm text-neutral-400 italic line-clamp-2">"{item.title}"</span>
                                                   </div>
                                                )}

                                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                                   {item.victim_country && <span className="flex items-center gap-1.5 bg-black/40 px-2 py-1 rounded text-xs text-neutral-300 border border-white/5"><MapPin className="h-3 w-3 text-emerald-400" /> {item.victim_country}</span>}
                                                   {item.victim_industry && <span className="flex items-center gap-1.5 bg-black/40 px-2 py-1 rounded text-xs text-neutral-300 border border-white/5"><Target className="h-3 w-3 text-amber-400" /> {item.victim_industry}</span>}
                                                   {hasDomain && <span className="flex items-center gap-1.5 bg-sky-500/10 px-2 py-1 rounded text-xs text-sky-200 border border-sky-500/20 font-mono"><Globe className="h-3 w-3" /> {item.victim_domain || item.victim_site}</span>}
                                                  {item.network && <span className="flex items-center gap-1.5 bg-sky-500/10 px-2 py-1 rounded text-xs text-sky-200 border border-sky-500/20 font-mono"><Network className="h-3 w-3" /> {item.network}</span>}

                                                </div>
                                             </div>
                                             
                                             <div className="shrink-0 text-right">
                                                <div className="text-xs font-mono text-neutral-500">{formatDate(item.published_at)}</div>
                                             </div>
                                          </div>
                                       </div>
                                    </div>
                                 )})}
                              </div>
                           </div>
                        </>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* --- SAFETY WARNING MODAL --- */}
    <Transition.Root show={!!redirectUrl} as={Fragment}>
        <Dialog as="div" className="relative z-[300]" onClose={() => setRedirectUrl(null)}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-black/75 transition-opacity" />
            </Transition.Child>
            
            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-[#0f172a] border border-red-500/30 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <div className="bg-[#0f172a] px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                                        <AlertOctagon className="h-6 w-6 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-white">
                                            External Redirection Warning
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-neutral-300">
                                                You are about to leave the platform and visit an external URL. 
                                                This link often leads to a <strong>hacker forum</strong>, <strong>leak site</strong>, or <strong>unverified source</strong>.
                                            </p>
                                            <div className="mt-4 rounded-md bg-red-950/30 border border-red-500/20 p-3">
                                                <p className="text-xs text-red-300 font-mono break-all">
                                                    {redirectUrl}
                                                </p>
                                            </div>
                                            <p className="mt-4 text-xs text-neutral-400">
                                                <strong>Security Tip:</strong> We recommend opening this link in a sandboxed environment or a dedicated VM.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-[#06101e] px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 border-t border-white/5">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                    onClick={() => {
                                        window.open(redirectUrl, '_blank');
                                        setRedirectUrl(null);
                                    }}
                                >
                                    Proceed (Unsafe)
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white/5 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-white/10 hover:bg-white/10 sm:mt-0 sm:w-auto"
                                    onClick={() => setRedirectUrl(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition.Root>
    </>
  );
}